import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  FatcaStatusFFI,
  FatcaStatusNFFE,
  shuwGiinFatcaStatusFFI,
} from '~/modules/identification/forms/FatcaAdditionalInformation/constants/constants.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';

export const SubstantialUSOwners = Yup.object().shape({
  id: Yup.string().nullable(),
  lastName: Yup.string().nullable(),
  firstName: Yup.string().nullable(),
  middleName: Yup.string().nullable(),
  lastNameEng: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .matches(regularExpressions.LATIN_NAME, errorMessages.LATIN_CHARACTERS_ONLY)
    .max(120, errorMessages.MAX_LENGTH(120)),
  firstNameEng: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .matches(regularExpressions.LATIN_NAME, errorMessages.LATIN_CHARACTERS_ONLY)
    .max(120, errorMessages.MAX_LENGTH(120)),
  middleNameEng: Yup.string()
    .nullable()
    .notRequired()
    .matches(regularExpressions.LATIN_NAME, {
      message: errorMessages.LATIN_CHARACTERS_ONLY,
      excludeEmptyString: true,
    })
    .max(120, errorMessages.MAX_LENGTH(120)),
  tin: Yup.string().nullable().notRequired().matches(regularExpressions.TIN_FORMAT, {
    message: errorMessages.INVALID_TIN_FORMAT,
    excludeEmptyString: true,
  }),
  countryEng: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .matches(regularExpressions.LATIN_NAME, errorMessages.LATIN_CHARACTERS_ONLY)
    .max(70, errorMessages.MAX_LENGTH(70)),
  cityEng: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .matches(regularExpressions.LATIN_NAME, errorMessages.LATIN_CHARACTERS_ONLY)
    .max(70, errorMessages.MAX_LENGTH(70)),
});

export const FatcaAdditionalInformationValidationSchema = (
  isUSTaxResident: boolean,
  isForeignFinancialUnderFATCA: boolean,
  isControllingPersonUSTaxResident: boolean
) =>
  Yup.object().shape({
    usEntityInfo: Yup.object().shape({
      clientFullName: Yup.string().nullable(),
      clientFullNameEng: isUSTaxResident
        ? Yup.string()
            .required(errorMessages.FIELD_REQUIRED)
            .matches(regularExpressions.LATIN_NUMBERS_SYMBOLS, errorMessages.LATIN_CHARACTERS_ONLY)
        : Yup.string().nullable().notRequired(),
      tinEin: isUSTaxResident
        ? Yup.string()
            .nullable()
            .required(errorMessages.FIELD_REQUIRED)
            .matches(regularExpressions.EIN_FORMAT, errorMessages.INVALID_EIN_FORMAT)
        : Yup.string().nullable().notRequired(),
      fatcaStatus: isUSTaxResident
        ? Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED)
        : Yup.string().nullable().notRequired(),
    }),
    foreignFinancialInstitutionInfo: Yup.object().shape({
      clientFullName: Yup.string().nullable(),
      fatcaStatusFFI: isForeignFinancialUnderFATCA
        ? Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED)
        : Yup.string().nullable().notRequired(),
      otherFatcaStatusFFI: isForeignFinancialUnderFATCA
        ? Yup.string()
            .nullable()
            .when('fatcaStatusFFI', function ([fatcaStatusFFI], schema) {
              return fatcaStatusFFI === FatcaStatusFFI.OTHER
                ? schema
                    .required(errorMessages.FIELD_REQUIRED)
                    .max(500, errorMessages.MAX_LENGTH(500))
                : schema.notRequired();
            })
        : Yup.string().nullable().notRequired(),
      giin: isForeignFinancialUnderFATCA
        ? Yup.string()
            .nullable()
            .when('fatcaStatusFFI', function ([fatcaStatusFFI], schema) {
              if (fatcaStatusFFI === FatcaStatusFFI.OTHER) {
                return schema.notRequired().matches(regularExpressions.REGISTRY_RECORD_GIIN, {
                  message: errorMessages.GIIN_ENTRY_FORMAT,
                  excludeEmptyString: true,
                });
              }
              if (shuwGiinFatcaStatusFFI.includes(fatcaStatusFFI)) {
                return schema
                  .required(errorMessages.FIELD_REQUIRED)
                  .matches(regularExpressions.REGISTRY_RECORD_GIIN, {
                    message: errorMessages.GIIN_ENTRY_FORMAT,
                    excludeEmptyString: true,
                  });
              }
              return schema.notRequired();
            })
        : Yup.string().nullable().notRequired(),
    }),
    nffeInfo: Yup.object().shape({
      fatcaStatusNFFE: isControllingPersonUSTaxResident
        ? Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED)
        : Yup.string().nullable().notRequired(),
      otherFatcaStatusNFFE: isControllingPersonUSTaxResident
        ? Yup.string()
            .nullable()
            .when('fatcaStatusNFFE', function ([fatcaStatusNFFE], schema) {
              return fatcaStatusNFFE === FatcaStatusNFFE.OTHER
                ? schema
                    .required(errorMessages.FIELD_REQUIRED)
                    .max(500, errorMessages.MAX_LENGTH(500))
                : schema.notRequired();
            })
        : Yup.string().nullable().notRequired(),
      substantialUSOwners: Yup.array()
        .nullable()
        .when('fatcaStatusNFFE', function ([fatcaStatusNFFE], schema) {
          const ownersRequired =
            isControllingPersonUSTaxResident &&
            fatcaStatusNFFE === FatcaStatusNFFE.PASSIVE_NFFE_WITH_SUBSTANTIAL_US_OWNERS;

          return ownersRequired ? schema.of(SubstantialUSOwners) : schema.notRequired();
        }),
    }),
  });
